import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Breadcrumbs,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getCustomers } from "../api/customer";
import { getLocationsByCustomerId } from "../api/location";
import { assignDeviceToCustomerAndLocation } from "../api/devices";

const DeviceAssignmentModal = ({
  open,
  onClose,
  deviceId,
  deviceName = "Unnamed Device",
  onAssignmentSuccess,
  onAssignmentError,
}) => {
  const navigate = useNavigate();

  // Modal states
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectionStep, setSelectionStep] = useState("customer");

  // Initialize modal when opened
  React.useEffect(() => {
    if (open) {
      handleInitializeModal();
    }
  }, [open]);

  const handleInitializeModal = async () => {
    setModalLoading(true);
    try {
      const customerData = await getCustomers();
      setCustomers(customerData);
      setFilteredCustomers(customerData.slice(0, 10)); // Show first 10
    } catch (error) {
      console.error("Error loading customers:", error);
      if (onAssignmentError) {
        onAssignmentError("Failed to load customers");
      }
    } finally {
      setModalLoading(false);
    }
  };

  // Handler for customer search
  const handleCustomerSearch = (searchTerm) => {
    setCustomerSearchTerm(searchTerm);
    if (!searchTerm.trim()) {
      setFilteredCustomers(customers.slice(0, 10));
    } else {
      const filtered = customers
        .filter((customer) =>
          customer.uk_name?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
        .slice(0, 10);
      setFilteredCustomers(filtered);
    }
  };

  // Handler for customer selection
  const handleCustomerSelect = async (customer) => {
    setSelectedCustomer(customer);
    setSelectionStep("location");
    setModalLoading(true);

    try {
      const locationData = await getLocationsByCustomerId(customer.pk_id);
      setLocations(locationData.data);
      console.log("Locations for customer:", locationData.data);
    } catch (error) {
      console.error("Error loading locations:", error);
      if (onAssignmentError) {
        onAssignmentError("Failed to load locations");
      }
    } finally {
      setModalLoading(false);
    }
  };

  // Handler for location selection
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setSelectionStep("building");
  };

  // Handler for building selection
  const handleBuildingSelect = (building) => {
    setSelectedBuilding(building);
    setSelectionStep("department");
  };

  // Handler for department selection
  const handleDepartmentSelect = (department) => {
    setSelectedDepartment(department);
    setSelectionStep("room");
  };

  // Handler for room selection
  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
  };

  // Handler for breadcrumb navigation
  const handleBreadcrumbClick = (step) => {
    setSelectionStep(step);
    // Reset subsequent selections based on step
    if (step === "customer") {
      setSelectedCustomer(null);
      setSelectedLocation(null);
      setSelectedBuilding(null);
      setSelectedDepartment(null);
      setSelectedRoom(null);
    } else if (step === "location") {
      setSelectedLocation(null);
      setSelectedBuilding(null);
      setSelectedDepartment(null);
      setSelectedRoom(null);
    } else if (step === "building") {
      setSelectedBuilding(null);
      setSelectedDepartment(null);
      setSelectedRoom(null);
    } else if (step === "department") {
      setSelectedDepartment(null);
      setSelectedRoom(null);
    }
  };

  // Render breadcrumbs
  const renderBreadcrumbs = () => {
    const crumbs = [];

    crumbs.push(
      <Button
        key="customer"
        size="small"
        color={selectionStep === "customer" ? "primary" : "default"}
        onClick={() => handleBreadcrumbClick("customer")}
      >
        Customer
      </Button>,
    );

    if (selectedCustomer) {
      crumbs.push(
        <Button
          key="location"
          size="small"
          color={selectionStep === "location" ? "primary" : "default"}
          onClick={() => handleBreadcrumbClick("location")}
        >
          Location
        </Button>,
      );
    }

    if (selectedLocation) {
      crumbs.push(
        <Button
          key="building"
          size="small"
          color={selectionStep === "building" ? "primary" : "default"}
          onClick={() => handleBreadcrumbClick("building")}
        >
          Building
        </Button>,
      );
    }

    if (selectedBuilding) {
      crumbs.push(
        <Button
          key="department"
          size="small"
          color={selectionStep === "department" ? "primary" : "default"}
          onClick={() => handleBreadcrumbClick("department")}
        >
          Department
        </Button>,
      );
    }

    return (
      <Breadcrumbs sx={{ marginBottom: 2 }}>
        {crumbs.map((crumb, index) => (
          <span key={index} style={{ cursor: "pointer" }}>
            {crumb}
          </span>
        ))}
      </Breadcrumbs>
    );
  };

  // Close modal and reset state
  const handleCloseModal = () => {
    setSelectedCustomer(null);
    setSelectedLocation(null);
    setSelectedBuilding(null);
    setSelectedDepartment(null);
    setSelectedRoom(null);
    setCustomers([]);
    setFilteredCustomers([]);
    setCustomerSearchTerm("");
    setLocations([]);
    setSelectionStep("customer");
    onClose();
  };

  const handleSubmitAssignment = async () => {
    const submittedRoomId = selectedRoom.pk_id;
    setModalLoading(true);

    try {
      const result = await assignDeviceToCustomerAndLocation(
        deviceId,
        submittedRoomId,
      );

      setModalLoading(false);

      if (result.success) {
        handleCloseModal();
        if (onAssignmentSuccess) {
          onAssignmentSuccess(result, {
            customer: selectedCustomer,
            location: selectedLocation,
            building: selectedBuilding,
            department: selectedDepartment,
            room: selectedRoom,
          });
        }
      } else {
        if (onAssignmentError) {
          onAssignmentError(`Failed to assign device: ${result.error}`);
        }
      }
    } catch (error) {
      setModalLoading(false);
      if (onAssignmentError) {
        onAssignmentError(`Error assigning device: ${error.message}`);
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth>
      <DialogTitle>Assign Device: {deviceName}</DialogTitle>
      <DialogContent>
        {/* Breadcrumb Navigation */}
        {renderBreadcrumbs()}

        {modalLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {!selectedCustomer ? (
              // Customer Selection Step
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Select Customer
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search customers..."
                  value={customerSearchTerm}
                  onChange={(e) => handleCustomerSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <List sx={{ maxHeight: 300, overflow: "auto" }}>
                  {filteredCustomers.map((customer) => (
                    <ListItem key={customer.pk_id} disablePadding>
                      <ListItemButton
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <ListItemText
                          primary={customer.uk_name}
                          secondary={customer.customer_address}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                {filteredCustomers.length === 0 && !modalLoading && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", p: 2 }}
                  >
                    No customers found
                  </Typography>
                )}
              </Box>
            ) : (
              // Multi-step Selection based on selectionStep
              <Box>
                {/* Current selection display */}
                <Box sx={{ mb: 2 }}>
                  {selectedCustomer && (
                    <Typography variant="body2" color="text.secondary">
                      Customer: {selectedCustomer.uk_name}
                    </Typography>
                  )}
                  {selectedLocation && (
                    <Typography variant="body2" color="text.secondary">
                      Location: {selectedLocation.location_name}
                    </Typography>
                  )}
                  {selectedBuilding && (
                    <Typography variant="body2" color="text.secondary">
                      Building: {selectedBuilding.building_name}
                    </Typography>
                  )}
                  {selectedDepartment && (
                    <Typography variant="body2" color="text.secondary">
                      Department: {selectedDepartment.department_name}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Conditional rendering based on selection step */}
                {selectionStep === "location" && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Select Location
                    </Typography>

                    {locations.length > 0 ? (
                      <List sx={{ maxHeight: 300, overflow: "auto" }}>
                        {locations.map((location) => {
                          const hasBuildings =
                            location.buildings && location.buildings.length > 0;
                          return (
                            <ListItem key={location.pk_id} disablePadding>
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Box sx={{ flexGrow: 1 }}>
                                  <ListItemButton
                                    onClick={() =>
                                      hasBuildings
                                        ? handleLocationSelect(location)
                                        : null
                                    }
                                    selected={
                                      selectedLocation?.pk_id === location.pk_id
                                    }
                                    disabled={!hasBuildings}
                                    sx={{
                                      opacity: hasBuildings ? 1 : 0.5,
                                      cursor: hasBuildings
                                        ? "pointer"
                                        : "default",
                                    }}
                                  >
                                    <ListItemText
                                      primary={location.location_name}
                                      secondary={location.location_address}
                                    />
                                  </ListItemButton>
                                </Box>
                                {!hasBuildings && (
                                  <Box
                                    sx={{
                                      ml: 2,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontSize: "0.8rem" }}
                                    >
                                      Building, department, and room need to be
                                      created
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => {
                                        navigate(
                                          `/customer/${selectedCustomer.pk_id}`,
                                        );
                                        handleCloseModal();
                                      }}
                                      sx={{ minWidth: "unset", px: 1 }}
                                    >
                                      Setup
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    ) : (
                      <Box sx={{ textAlign: "center", p: 3 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          No locations found for this customer
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            navigate(`/customer/${selectedCustomer.pk_id}`);
                            handleCloseModal();
                          }}
                        >
                          Create a Location
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Building Selection */}
                {selectionStep === "building" && selectedLocation && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Select Building
                    </Typography>

                    {selectedLocation.buildings &&
                    selectedLocation.buildings.length > 0 ? (
                      <List sx={{ maxHeight: 300, overflow: "auto" }}>
                        {selectedLocation.buildings.map((building) => (
                          <ListItem key={building.pk_id} disablePadding>
                            <ListItemButton
                              onClick={() => handleBuildingSelect(building)}
                              selected={
                                selectedBuilding?.pk_id === building.pk_id
                              }
                            >
                              <ListItemText
                                primary={building.building_name}
                                secondary={building.building_address}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ textAlign: "center", p: 3 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          No buildings found for this location
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            navigate(`/customer/${selectedCustomer.pk_id}`);
                            handleCloseModal();
                          }}
                        >
                          Create a Building
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Department Selection */}
                {selectionStep === "department" && selectedBuilding && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Select Department
                    </Typography>

                    {selectedBuilding.departments &&
                    selectedBuilding.departments.length > 0 ? (
                      <List sx={{ maxHeight: 300, overflow: "auto" }}>
                        {selectedBuilding.departments.map((department) => (
                          <ListItem key={department.pk_id} disablePadding>
                            <ListItemButton
                              onClick={() => handleDepartmentSelect(department)}
                              selected={
                                selectedDepartment?.pk_id === department.pk_id
                              }
                            >
                              <ListItemText
                                primary={department.department_name}
                                secondary={`Floor: ${
                                  department.department_floor || "N/A"
                                }`}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ textAlign: "center", p: 3 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          No departments found for this building
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            navigate(`/customer/${selectedCustomer.pk_id}`);
                            handleCloseModal();
                          }}
                        >
                          Create a Department
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Room Selection */}
                {selectionStep === "room" && selectedDepartment && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Select Room
                    </Typography>

                    {selectedDepartment.rooms &&
                    selectedDepartment.rooms.length > 0 ? (
                      <List sx={{ maxHeight: 300, overflow: "auto" }}>
                        {selectedDepartment.rooms.map((room) => (
                          <ListItem key={room.pk_id} disablePadding>
                            <ListItemButton
                              onClick={() => handleRoomSelect(room)}
                              selected={selectedRoom?.pk_id === room.pk_id}
                            >
                              <ListItemText primary={room.room_name} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ textAlign: "center", p: 3 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          No rooms found for this department
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            navigate(`/customer/${selectedCustomer.pk_id}`);
                            handleCloseModal();
                          }}
                        >
                          Create a Room
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Final assignment confirmation */}
                {selectedRoom && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "ffffff",
                      borderRadius: 1,
                      borderColor: "success.main",
                      borderWidth: 2,
                      borderStyle: "solid",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="success.dark"
                      sx={{ fontWeight: "bold" }}
                    >
                      Ready to assign device to:
                    </Typography>
                    <Typography variant="body2" color="success.dark">
                      • Customer: {selectedCustomer.uk_name}
                    </Typography>
                    <Typography variant="body2" color="success.dark">
                      • Location: {selectedLocation.location_name}
                    </Typography>
                    <Typography variant="body2" color="success.dark">
                      • Building: {selectedBuilding.building_name}
                    </Typography>
                    <Typography variant="body2" color="success.dark">
                      • Department: {selectedDepartment.department_name}
                    </Typography>
                    <Typography variant="body2" color="success.dark">
                      • Room: {selectedRoom.room_name}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal}>Cancel</Button>
        {selectedCustomer && selectionStep !== "customer" && (
          <Button
            onClick={() => handleBreadcrumbClick("customer")}
            color="secondary"
          >
            Back to Customers
          </Button>
        )}
        {selectedRoom && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitAssignment}
            disabled={modalLoading}
          >
            {modalLoading ? "Assigning..." : "Assign Device"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeviceAssignmentModal;
